// @ts-ignore
import type {StrapiUser} from "@nuxtjs/strapi/dist/runtime/types";
import type {Ref} from "vue";
import userLocalFactory from "~/libs/factories/UserLocalFactory";
import type UserLocal from "~/libs/interfaces/UserLocal";
import type GameUserLocal from "~/libs/interfaces/GameUserLocal";
import type GameUserStrapi from "~/libs/interfaces/GameUserStrapi";
import gameUserLocalFactory from "~/libs/factories/GameUserLocalFactory";
import type UserGameRegisterPayload from "~/libs/interfaces/UserGameRegisterPayload";

export const useUserStore = defineStore('user', {
  state: () => {
    const state: {
      isLoading: Ref<boolean>,
      lastSync: Record<string, Date>,
      user: UserLocal,
      gameUsers: Ref<Record<string, GameUserLocal>>
    } = {
      isLoading: ref(false),
      lastSync: {},
      user: reactive({}),
      gameUsers: ref({}),
    }
    return state
  },
  getters: {
    isLoggedIn(state): boolean {
      return !!(state.user && state.user?.confirmed && !state.user?.blocked)
    }
  },
  actions: {
    async fetchUser() {
      const logger = useFrontendLogger()
      const { findOne } = useStrapi()

      const populate = {
        game_users: true
      }

      const params = {
        populate
      }

      try {
        const result = await findOne<StrapiUser>('users', 'me', params)
        this.setItem(userLocalFactory.createUserFromStrapi(result))
      } catch (e) {
        logger?.error('useUserStore:fetchUser - exception', {
          errorDetail: e,
        })
        this.isLoading = false
        this.user = {}
      }
    },
    logout() {
      // Clear the Strapi JWT cookie (psc-api-jwt) on the client.
      const { setToken } = useStrapiAuth()
      setToken(null)

      // Clear local user state.
      if (this.user) {
        const keys = Object.keys(this.user)
        keys.forEach((key: string) => {
          if (this.user.hasOwnProperty(key)) {
            delete this.user[key as keyof UserLocal];
          }
        })
      }

      // Redirect to the Nitro logout route: clears server cookies + Sentinel end-session.
      if (import.meta.client) {
        const config = useRuntimeConfig()
        const ssoLogoutPath = String(config.public.ssoLogoutPath || '/auth/sso/logout')
        window.location.href = ssoLogoutPath
      }
    },
    async updateMe(me: Partial<UserLocal> = {}) {
      const logger = useFrontendLogger()
      const { update } = useStrapi()
      this.isLoading = true
      try {
        if (this.user && this.user.id) {
          const result = await update<StrapiUser>('user', 'me', me)
          this.setItem(userLocalFactory.createUserFromStrapi(result))
        }
        this.isLoading = false
      } catch (e) {
        logger?.error('useUserStore:updateMe - exception', {
          errorDetail: e,
        })
        this.isLoading = false
        throw e
      }
    },
    setItem(item?: UserLocal): void {
      if (item) {
        this.user = item
      } else {
        this.user = {}
      }
    },
    getUserGame(gameDocumentId?: string): GameUserLocal | null {
      if (!gameDocumentId) {
        return null
      }
      return this.gameUsers[gameDocumentId]
    },
    async fetchUserGame(gameDocumentId?: string): Promise<GameUserLocal | null> {
      if (!gameDocumentId) {
        return null
      }

      const logger = useFrontendLogger()
      const { find } = useStrapi()

      if (!this.user?.id) {
        return null
      }

      const filters = {
        game: {
          documentId: {
            $eq: gameDocumentId,
          }
        }
      }

      const populate = {
        game: {
          fields: ['id', 'documentId','title']
        },
        user: {
          fields: ['id', 'documentId','email']
        }
      }

      const params = {
        filters,
        populate,
        pagination: {
          start: 0,
          limit: 1,
        }
      }

      try {
        const result = await find<GameUserStrapi>('game-users/my-games', params)
        //@ts-ignore
        const items = result.data || []
        this.isLoading = false
        if (items[0]) {
          const gameUser = gameUserLocalFactory.createGameUserFromStrapi(items[0])
          if (gameUser) {
            this.gameUsers[gameDocumentId] = gameUser
          }
          return this.gameUsers[gameDocumentId]
        }
        return null
      } catch (e) {
        logger?.error('useUserStore:fetchUserGames - exception', {
          errorDetail: e,
          strapiCallParams: params,
        })
        this.isLoading = false
        throw e
      }
    },
    async registerToGame(payload: UserGameRegisterPayload): Promise<GameUserLocal | null> {
      const logger = useFrontendLogger()
      const { create } = useStrapi()
      try {
        if (payload.game && payload.psc && payload.hasAgeConsent) {
          const userGame = await create<any>('game-users/register', payload)
          const gameUserLocal = gameUserLocalFactory.createGameUserFromStrapi(userGame as unknown as GameUserStrapi)
          if (gameUserLocal) {
            this.gameUsers[payload.game] = gameUserLocal
          }
          return this.gameUsers[payload.game]
        }
      } catch (e) {
        logger?.error('useUserGameDetailStore:registerToGame - exception', {
          errorDetail: e,
        })
        throw e
      }
      return null
    },
  },
})
